"use client";

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type AdminTheme = "light" | "dark";
export type GridDensity = "comfortable" | "compact";
export type SelectedSuburbPreference = {
  id: string;
  slug: string;
  name: string;
} | null;

export const PREFERENCES_STORAGE_KEY = "neighbourhood-preferences";
export const LEGACY_THEME_STORAGE_KEY = "neighbourhood-admin-theme";

type PreferencesState = {
  theme: AdminTheme;
  sidebarCollapsed: boolean;
  tableDensity: GridDensity;
  viewModes: Record<string, string>;
  selectedSuburb: SelectedSuburbPreference;
  hydrated: boolean;
  setTheme: (theme: AdminTheme) => void;
  toggleTheme: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTableDensity: (density: GridDensity) => void;
  setViewMode: (scope: string, view: string) => void;
  setSelectedSuburb: (suburb: SelectedSuburbPreference) => void;
  setHydrated: (hydrated: boolean) => void;
};

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      theme: "light",
      sidebarCollapsed: false,
      tableDensity: "compact",
      viewModes: {},
      selectedSuburb: null,
      hydrated: false,
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set({ theme: get().theme === "dark" ? "light" : "dark" }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setTableDensity: (tableDensity) => set({ tableDensity }),
      setViewMode: (scope, view) => set((state) => ({
        viewModes: {
          ...state.viewModes,
          [scope]: view
        }
      })),
      setSelectedSuburb: (selectedSuburb) => set({ selectedSuburb }),
      setHydrated: (hydrated) => set({ hydrated })
    }),
    {
      name: PREFERENCES_STORAGE_KEY,
      version: 2,
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        tableDensity: state.tableDensity,
        viewModes: state.viewModes,
        selectedSuburb: state.selectedSuburb
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      }
    }
  )
);
